import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock,
  FileText,
  Filter,
  Info,
  Layers,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Tag,
  UserPlus,
  X,
} from 'lucide-react';
import {
  fetchProspectAISettings,
  fetchProspectKnowledge,
  fetchProspectLeads,
  fetchProspectSummary,
  markProspectConverted,
  sendConversationWhatsAppTemplate,
  sendProspectRegistrationLink,
  updateProspectAISettings,
  updateProspectKnowledge,
  updateProspectLeadAIMode,
  updateProspectLeadStage,
} from './api';
import ProspectLeadMiniChat from './ProspectLeadMiniChat';
import { socket, socketAuthPayload } from '../../admin/chat/socket';

const PAGE_SIZE = 30;

const STAGE_LABELS = {
  new: 'Nuevo',
  engaged: 'Conversando',
  possible_lead: 'Posible lead',
  qualified: 'Calificado',
  handoff: 'Handoff',
  operational_issue: 'Problema operativo',
  registration_sent: 'Registro enviado',
  converted: 'Convertido',
  closed_lost: 'Perdido',
  not_a_lead: 'No lead',
  existing_user: 'Usuario existente',
};

const STATUS_LABELS = {
  open: 'Abierto',
  qualified: 'Calificado',
  handoff: 'Handoff',
  converted: 'Convertido',
  closed_lost: 'Perdido',
  not_a_lead: 'No lead',
  existing_user: 'Usuario existente',
};

const STATUS_FILTERS = [
  ['remaining', 'Activos'],
  ['open', 'Abiertos'],
  ['qualified', 'Calificados'],
  ['handoff', 'Handoff'],
  ['converted', 'Convertidos'],
  ['closed_lost', 'Perdidos'],
  ['not_a_lead', 'No leads'],
  ['existing_user', 'Existentes'],
  ['all', 'Todos'],
];

const WINDOW_FILTERS = [
  ['all', 'Todos'],
  ['within_24', '<=24h'],
  ['24_48', '24-48h'],
  ['48_72', '48-72h'],
  ['over_72', '>72h'],
];

const SEGREGATION_ACTIONS = [
  { id: 'possible_lead', label: 'Posible lead', stage: 'possible_lead', status: 'open', className: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
  { id: 'qualified', label: 'Calificado', stage: 'qualified', status: 'qualified', className: 'bg-purple-50 text-purple-700 hover:bg-purple-100' },
  { id: 'registration_sent', label: 'Enviar registro', action: 'registration', className: 'bg-[#B1336E]/10 text-[#B1336E] hover:bg-[#B1336E]/15' },
  { id: 'converted', label: 'Convertido', action: 'converted', className: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
  { id: 'existing_user', label: 'Usuario existente', stage: 'existing_user', status: 'existing_user', className: 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100' },
  { id: 'operational_issue', label: 'Problema operativo', stage: 'operational_issue', status: 'handoff', reason: 'Segregado como problema operativo desde prospect drawer.', className: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
  { id: 'not_a_lead', label: 'No lead', stage: 'not_a_lead', status: 'not_a_lead', className: 'bg-slate-100 text-slate-600 hover:bg-slate-200' },
  { id: 'spam', label: 'Spam', stage: 'not_a_lead', status: 'not_a_lead', reason: 'Marcado como spam desde prospect drawer.', className: 'bg-red-50 text-red-700 hover:bg-red-100' },
  { id: 'closed_lost', label: 'Perdido', stage: 'closed_lost', status: 'closed_lost', className: 'bg-slate-100 text-slate-600 hover:bg-slate-200' },
  { id: 'handoff', label: 'Handoff', stage: 'handoff', status: 'handoff', reason: 'Requiere intervencion humana desde prospect drawer.', className: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
];

function compactError(err, fallback) {
  const payload = err?.response?.data;
  return payload?.message || payload?.reason || payload?.error || err?.message || fallback;
}

function formatDate(value) {
  if (!value) return 'No registrado';
  try {
    return new Date(value).toLocaleString('es-PE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'No registrado';
  }
}

function formatHours(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return 'sin inbound';
  const hours = Number(value);
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  return `${Math.round(hours * 10) / 10}h`;
}

function labelClass(label) {
  if (label === 'hot') return 'bg-red-100 text-red-700 border-red-200';
  if (label === 'warm') return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

function stageClass(stage, requiresHuman) {
  if (requiresHuman || stage === 'handoff' || stage === 'operational_issue') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (stage === 'converted') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (stage === 'registration_sent') return 'bg-sky-100 text-sky-800 border-sky-200';
  if (stage === 'qualified') return 'bg-purple-100 text-purple-800 border-purple-200';
  if (stage === 'possible_lead') return 'bg-blue-100 text-blue-800 border-blue-200';
  if (stage === 'existing_user') return 'bg-cyan-100 text-cyan-800 border-cyan-200';
  if (stage === 'closed_lost' || stage === 'not_a_lead') return 'bg-slate-100 text-slate-500 border-slate-200';
  return 'bg-blue-50 text-blue-700 border-blue-100';
}

function whatsappWindowClass(bucket) {
  if (bucket === 'within_24') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (bucket === '24_48') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (bucket === '48_72') return 'bg-orange-100 text-orange-800 border-orange-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

function whatsappWindowLabel(windowState = {}) {
  const bucket = windowState?.bucket;
  if (bucket === 'within_24') return `Libre ${formatHours(windowState.hours_since_last_inbound)}`;
  if (bucket === '24_48') return `24-48h ${formatHours(windowState.hours_since_last_inbound)}`;
  if (bucket === '48_72') return `48-72h ${formatHours(windowState.hours_since_last_inbound)}`;
  return `>72h ${formatHours(windowState?.hours_since_last_inbound)}`;
}

function mergeLeads(existing, incoming) {
  const map = new Map();
  [...existing, ...incoming].forEach(item => {
    if (item?.id) map.set(item.id, { ...(map.get(item.id) || {}), ...item });
  });
  return Array.from(map.values()).sort((a, b) => {
    const humanA = a.requires_human ? 1 : 0;
    const humanB = b.requires_human ? 1 : 0;
    if (humanA !== humanB) return humanB - humanA;
    const scoreA = Number(a.qualification_score || 0);
    const scoreB = Number(b.qualification_score || 0);
    if (scoreA !== scoreB) return scoreB - scoreA;
    return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
  });
}

function upsertLead(existing, incoming) {
  if (!incoming?.id) return existing;
  return mergeLeads(existing.filter(item => item.id !== incoming.id), [incoming]);
}

function flattenKnowledge(payload) {
  const grouped = payload?.items_by_category || payload || {};
  return Object.entries(grouped).flatMap(([category, rows]) =>
    (rows || []).map(row => ({ ...row, category: row.category || category }))
  );
}

function getAdReferral(lead) {
  return (
    lead?.source_metadata?.ad_referral ||
    lead?.latest_message?.ai_metadata?.ad_referral ||
    lead?.latest_message?.ai_metadata?.whatsapp_payload?.referral ||
    null
  );
}

function FieldRow({ label, value }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
      <p className="text-[10px] font-black uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 break-words text-xs font-bold text-slate-800">{value || 'No registrado'}</p>
    </div>
  );
}

function LeadListItem({ lead, selected, onSelect }) {
  const windowState = lead.whatsapp_window || {};
  return (
    <button
      type="button"
      onClick={() => onSelect(lead)}
      className={`block w-full border-b border-slate-100 p-3 text-left transition ${selected ? 'bg-[#B1336E]/5 ring-1 ring-inset ring-[#B1336E]/30' : 'bg-white hover:bg-slate-50'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate text-sm font-extrabold text-slate-950">{lead.phone_number || `Lead #${lead.id}`}</p>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-extrabold ${stageClass(lead.stage, lead.requires_human)}`}>
              {lead.requires_human ? 'Humano' : (STAGE_LABELS[lead.stage] || lead.stage || 'Nuevo')}
            </span>
          </div>
          <p className="mt-1 truncate text-[11px] font-semibold text-slate-500">
            {lead.current_intent || 'sin intent'} · {formatDate(lead.updated_at)}
          </p>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-black ${whatsappWindowClass(windowState.bucket)}`}>
          {whatsappWindowLabel(windowState)}
        </span>
      </div>
      <p className="mt-2 line-clamp-2 text-xs font-semibold text-slate-700">
        {lead.latest_message?.content || lead.ai_summary || 'Sin mensajes recientes'}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-extrabold ${labelClass(lead.qualification_label)}`}>
          {lead.qualification_label || 'cold'} {lead.qualification_score || 0}
        </span>
        {lead.ai_auto_enabled ? (
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-black text-emerald-700">AI lead ON</span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-black text-slate-500">AI lead OFF</span>
        )}
        {getAdReferral(lead) && (
          <span className="rounded-full bg-fuchsia-50 px-2 py-0.5 text-[10px] font-black text-fuchsia-700">Ad referral</span>
        )}
      </div>
      {lead.requires_human && (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-bold text-amber-800">
          {lead.handoff_reason || 'Requiere revision humana'}
        </p>
      )}
    </button>
  );
}

function LeadDetailPanel({ lead, workspace, onSegregate, saving }) {
  const captured = lead?.captured_data || {};
  const source = lead?.source_metadata || {};
  const referral = getAdReferral(lead);
  const events = workspace?.events || [];
  const windowState = lead?.whatsapp_window || workspace?.whatsapp_window || {};

  if (!lead) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm font-semibold text-slate-500">
        Selecciona un lead para ver detalle comercial, metadata y acciones de segregacion.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-black uppercase tracking-wide text-slate-400">Lead detail</p>
            <h3 className="mt-1 break-all text-base font-black text-slate-950">{lead.phone_number || `Lead #${lead.id}`}</h3>
          </div>
          <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black ${whatsappWindowClass(windowState.bucket)}`}>
            {whatsappWindowLabel(windowState)}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <FieldRow label="Stage" value={STAGE_LABELS[lead.stage] || lead.stage} />
          <FieldRow label="Status" value={STATUS_LABELS[lead.status] || lead.status} />
          <FieldRow label="Intent" value={lead.current_intent} />
          <FieldRow label="Score" value={`${lead.qualification_label || 'cold'} · ${lead.qualification_score || 0}`} />
          <FieldRow label="AI mode" value={lead.ai_auto_enabled ? 'Lead AI ON' : 'Lead AI OFF'} />
          <FieldRow label="Handoff" value={lead.requires_human ? (lead.handoff_reason || 'Requiere humano') : 'No'} />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <Layers className="h-4 w-4 text-[#B1336E]" />
          <h4 className="text-sm font-black text-slate-900">Datos capturados</h4>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <FieldRow label="Volumen" value={captured.mentioned_order_volume != null ? String(captured.mentioned_order_volume) : null} />
          <FieldRow label="Lima" value={captured.interested_lima ? 'Si' : 'No detectado'} />
          <FieldRow label="Provincia" value={captured.interested_province ? 'Si' : 'No detectado'} />
          <FieldRow label="Fulfillment" value={captured.interested_fulfillment ? 'Si' : 'No detectado'} />
          <FieldRow label="Dropshipping" value={captured.interested_dropshipping ? 'Si' : 'No detectado'} />
          <FieldRow label="Distritos" value={(captured.districts || []).join(', ')} />
          <FieldRow label="Provincias" value={(captured.provinces || []).join(', ')} />
          <FieldRow label="Objeciones" value={(lead.objections || []).join(', ')} />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#B1336E]" />
          <h4 className="text-sm font-black text-slate-900">Tiempos operativos</h4>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <FieldRow label="Primer contacto" value={formatDate(lead.first_contact_at)} />
          <FieldRow label="Ultimo contacto" value={formatDate(lead.last_contact_at)} />
          <FieldRow label="Primera respuesta" value={formatDate(lead.first_response_at)} />
          <FieldRow label="Registro enviado" value={formatDate(lead.registration_link_sent_at)} />
          <FieldRow label="Convertido" value={formatDate(lead.converted_at)} />
          <FieldRow label="Cerrado" value={formatDate(lead.closed_at)} />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <Tag className="h-4 w-4 text-[#B1336E]" />
          <h4 className="text-sm font-black text-slate-900">Ad referral metadata</h4>
        </div>
        {referral ? (
          <div className="space-y-2 text-xs font-semibold text-slate-700">
            <FieldRow label="Headline" value={referral.headline || referral.body} />
            <FieldRow label="Source" value={referral.source_url || referral.source_type || referral.source_id} />
            <FieldRow label="CTWA" value={referral.ctwa_clid} />
            <FieldRow label="Media" value={referral.media_type || referral.image_url || referral.video_url} />
          </div>
        ) : (
          <p className="rounded-xl bg-slate-50 p-3 text-xs font-semibold text-slate-500">
            No hay referral de anuncio capturado para este lead. Cuando Meta envie <code>msg.referral</code>, se almacena en metadata estructurada.
          </p>
        )}
        {source.resolver_status && (
          <p className="mt-2 text-[11px] font-semibold text-slate-500">Resolver: {source.resolver_status}</p>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-[#B1336E]" />
          <h4 className="text-sm font-black text-slate-900">Segregacion</h4>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {SEGREGATION_ACTIONS.map(action => (
            <button
              key={action.id}
              type="button"
              onClick={() => onSegregate(lead, action)}
              disabled={saving}
              className={`rounded-xl px-3 py-2 text-left text-[11px] font-black disabled:opacity-50 ${action.className}`}
            >
              {action.label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[10px] font-semibold text-slate-500">
          Estas acciones escriben estado estructurado del lead para analitica y futuras automatizaciones.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2">
          <FileText className="h-4 w-4 text-[#B1336E]" />
          <h4 className="text-sm font-black text-slate-900">Eventos recientes</h4>
        </div>
        {events.length ? (
          <div className="max-h-44 space-y-2 overflow-y-auto pr-1">
            {events.slice(0, 10).map(event => (
              <div key={event.id} className="rounded-xl bg-slate-50 p-2">
                <p className="text-[11px] font-black text-slate-800">{event.event_type}</p>
                <p className="text-[10px] font-semibold text-slate-500">{formatDate(event.created_at)}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="rounded-xl bg-slate-50 p-3 text-xs font-semibold text-slate-500">Sin eventos recientes en workspace.</p>
        )}
      </div>
    </div>
  );
}

function KnowledgePanel({ knowledge, edits, loading, savingId, onEdit, onSave, onRefresh }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#B1336E]" />
          <h4 className="text-sm font-black text-slate-900">Knowledge comercial</h4>
        </div>
        <button type="button" onClick={onRefresh} disabled={loading} className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200 disabled:opacity-50">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="mb-3 rounded-xl bg-purple-50 p-3 text-[11px] font-semibold text-purple-800">
        Respuestas comerciales activas que alimentan el agente seguro. Mantenerlas estructuradas mejora drafts, auto-respuestas y auditoria.
      </p>
      {loading && !knowledge.length ? (
        <div className="flex items-center justify-center gap-2 rounded-xl bg-slate-50 p-4 text-xs font-semibold text-slate-500">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Cargando knowledge...
        </div>
      ) : null}
      <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1">
        {knowledge.map(entry => {
          const edited = edits[entry.id] ?? entry.content ?? '';
          return (
            <div key={entry.id || `${entry.category}-${entry.title}`} className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[10px] font-black uppercase text-[#B1336E]">{entry.category}</p>
                  <p className="text-xs font-black text-slate-900">{entry.title}</p>
                </div>
                <div className="flex gap-1">
                  {entry.active && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-black text-emerald-700">Activo</span>}
                  {entry.safe_for_auto_reply && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[9px] font-black text-blue-700">Safe</span>}
                </div>
              </div>
              <textarea
                value={edited}
                onChange={event => onEdit(entry.id, event.target.value)}
                rows={4}
                className="mt-2 w-full resize-none rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold text-slate-700 outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
              />
              <button
                type="button"
                onClick={() => onSave(entry, edited)}
                disabled={!entry.id || savingId === entry.id || edited.trim() === (entry.content || '').trim()}
                className="mt-2 rounded-lg bg-slate-900 px-3 py-1.5 text-[10px] font-black text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {savingId === entry.id ? 'Guardando...' : 'Guardar knowledge'}
              </button>
            </div>
          );
        })}
        {!loading && !knowledge.length && (
          <p className="rounded-xl bg-slate-50 p-3 text-xs font-semibold text-slate-500">No hay knowledge disponible.</p>
        )}
      </div>
    </div>
  );
}

export default function ProspectLeadsDrawer({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState('remaining');
  const [windowBucket, setWindowBucket] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [nextOffset, setNextOffset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [knowledge, setKnowledge] = useState([]);
  const [knowledgeEdits, setKnowledgeEdits] = useState({});
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [knowledgeSavingId, setKnowledgeSavingId] = useState(null);

  const tableAvailable = settings?.table_available !== false && summary?.table_available !== false;
  const activeAuto = Boolean(settings?.safe_auto_enabled);
  const activeCount = summary?.remaining ?? 0;
  const conversionRate = useMemo(() => {
    const total = Number(summary?.total || 0);
    if (!total) return 0;
    return Math.round((Number(summary?.converted || 0) / total) * 100);
  }, [summary]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const handleLeadUpdated = useCallback((lead) => {
    if (!lead?.id) return;
    setItems(prev => upsertLead(prev, lead));
    setSelectedLead(prev => (prev?.id === lead.id ? { ...prev, ...lead } : prev));
  }, []);

  const fetchLeadPage = useCallback(async ({ reset = false, offset = 0 } = {}) => {
    if (!open) return;
    if (reset) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const data = await fetchProspectLeads({
        limit: PAGE_SIZE,
        offset,
        status,
        q: search || undefined,
        window_bucket: windowBucket,
      });
      const nextItems = data.items || [];
      if (reset) {
        setItems(nextItems);
      } else {
        setItems(prev => mergeLeads(prev, nextItems));
      }
      setHasMore(Boolean(data.has_more));
      setNextOffset(data.next_offset ?? null);
      if (reset) {
        setSelectedLead(prev => {
          if (!nextItems.length) return null;
          return (prev ? nextItems.find(item => item.id === prev.id) : null) || nextItems[0];
        });
      }
    } catch (err) {
      setError(compactError(err, 'No se pudieron cargar prospectos.'));
      if (reset) {
        setItems([]);
        setSelectedLead(null);
      }
    } finally {
      if (reset) setLoading(false);
      else setLoadingMore(false);
    }
  }, [open, search, status, windowBucket]);

  const loadDashboard = useCallback(async () => {
    if (!open) return;
    try {
      const [summaryData, settingsData] = await Promise.all([
        fetchProspectSummary({ days: 7 }),
        fetchProspectAISettings(),
      ]);
      setSummary(summaryData || null);
      setSettings(settingsData || null);
    } catch (err) {
      setError(compactError(err, 'No se pudo cargar resumen de prospectos.'));
    }
  }, [open]);

  const loadKnowledge = useCallback(async () => {
    if (!open) return;
    setKnowledgeLoading(true);
    try {
      const data = await fetchProspectKnowledge();
      setKnowledge(flattenKnowledge(data));
      setKnowledgeEdits({});
    } catch (err) {
      setError(compactError(err, 'No se pudo cargar knowledge comercial.'));
    } finally {
      setKnowledgeLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    fetchLeadPage({ reset: true, offset: 0 });
  }, [open, status, search, windowBucket, fetchLeadPage]);

  useEffect(() => {
    if (!open) return;
    loadDashboard();
    loadKnowledge();
  }, [open, loadDashboard, loadKnowledge]);

  useEffect(() => {
    if (!open) return undefined;
    const payload = socketAuthPayload({});
    const join = () => socket.emit('join_communications_admin', payload);
    const handleLead = (eventPayload) => {
      if (eventPayload?.summary) setSummary(eventPayload.summary);
      if (eventPayload?.lead) handleLeadUpdated(eventPayload.lead);
    };
    const handleSettings = (nextSettings) => setSettings(nextSettings || null);
    join();
    socket.on('connect', join);
    socket.on('communication_lead_updated', handleLead);
    socket.on('communication_lead_settings_updated', handleSettings);
    return () => {
      socket.off('connect', join);
      socket.off('communication_lead_updated', handleLead);
      socket.off('communication_lead_settings_updated', handleSettings);
    };
  }, [open, handleLeadUpdated]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore || nextOffset == null) return;
    fetchLeadPage({ reset: false, offset: nextOffset });
  }, [fetchLeadPage, hasMore, loading, loadingMore, nextOffset]);

  function handleListScroll(event) {
    const el = event.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 240) {
      loadMore();
    }
  }

  async function toggleGlobalAI() {
    if (!tableAvailable || saving) return;
    setSaving(true);
    setError(null);
    try {
      const next = await updateProspectAISettings({ safe_auto_enabled: !activeAuto });
      setSettings(next);
    } catch (err) {
      setError(compactError(err, 'No se pudo actualizar el agente.'));
    } finally {
      setSaving(false);
    }
  }

  async function toggleLeadAI(lead) {
    if (!lead?.id || saving) return;
    setSaving(true);
    setError(null);
    try {
      const data = await updateProspectLeadAIMode(lead.id, !lead.ai_auto_enabled);
      handleLeadUpdated(data.lead);
    } catch (err) {
      setError(compactError(err, 'No se pudo actualizar el AI del lead.'));
    } finally {
      setSaving(false);
    }
  }

  async function sendRegistration(lead) {
    if (!lead?.id || saving) return;
    setSaving(true);
    setError(null);
    try {
      let data;
      try {
        if (lead.whatsapp_window?.templates_required && lead.conversation_id) {
          data = await sendConversationWhatsAppTemplate(lead.conversation_id, 'prospect_registration');
        } else {
          data = await sendProspectRegistrationLink(lead.id);
        }
      } catch (err) {
        if (err?.response?.status === 428 && lead.conversation_id) {
          data = await sendConversationWhatsAppTemplate(lead.conversation_id, 'prospect_registration');
        } else {
          throw err;
        }
      }
      handleLeadUpdated(data.lead);
    } catch (err) {
      setError(compactError(err, 'No se pudo enviar el link/plantilla de registro.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleSegregation(lead, action) {
    if (!lead?.id || !action || saving) return;
    if (action.action === 'registration') {
      await sendRegistration(lead);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data = action.action === 'converted'
        ? await markProspectConverted(lead.id)
        : await updateProspectLeadStage(lead.id, {
          stage: action.stage,
          status: action.status,
          reason: action.reason,
        });
      handleLeadUpdated(data.lead);
    } catch (err) {
      setError(compactError(err, 'No se pudo actualizar la segregacion del lead.'));
    } finally {
      setSaving(false);
    }
  }

  async function saveKnowledge(entry, content) {
    if (!entry?.id || !content.trim()) return;
    setKnowledgeSavingId(entry.id);
    setError(null);
    try {
      const updated = await updateProspectKnowledge(entry.id, {
        content: content.trim(),
        active: entry.active,
        safe_for_auto_reply: entry.safe_for_auto_reply,
      });
      setKnowledge(prev => prev.map(item => (item.id === updated.id ? updated : item)));
      setKnowledgeEdits(prev => {
        const next = { ...prev };
        delete next[entry.id];
        return next;
      });
    } catch (err) {
      setError(compactError(err, 'No se pudo guardar knowledge.'));
    } finally {
      setKnowledgeSavingId(null);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-950/35">
      <div className="flex h-full w-full max-w-[1480px] flex-col bg-slate-100 shadow-2xl">
        <div className="border-b border-slate-200 bg-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-[#B1336E]" />
                <h2 className="font-montserrat text-lg font-extrabold text-slate-950">Prospect command drawer</h2>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                WhatsApp de ads separado del flujo operativo: lista, mini chat, detalle, knowledge y segregacion sin abrir el workspace admin.
              </p>
            </div>
            <button onClick={onClose} className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 overflow-hidden p-3 lg:grid-cols-[390px_minmax(420px,1fr)] xl:grid-cols-[390px_minmax(480px,1fr)_360px]">
          <section className="flex min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="border-b border-slate-200 p-3">
              <div className="grid grid-cols-4 gap-2">
                <div className="rounded-xl border border-blue-100 bg-blue-50 p-2 text-blue-800">
                  <p className="text-[9px] font-extrabold uppercase">Activos</p>
                  <p className="text-lg font-black">{activeCount}</p>
                </div>
                <div className="rounded-xl border border-blue-100 bg-blue-50 p-2 text-blue-800">
                  <p className="text-[9px] font-extrabold uppercase">Posibles</p>
                  <p className="text-lg font-black">{summary?.possible_lead || 0}</p>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-2 text-emerald-800">
                  <p className="text-[9px] font-extrabold uppercase">Conv.</p>
                  <p className="text-lg font-black">{summary?.converted || 0}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-slate-800">
                  <p className="text-[9px] font-extrabold uppercase">Rate</p>
                  <p className="text-lg font-black">{conversionRate}%</p>
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-extrabold text-slate-900">Agente comercial seguro</p>
                    <p className="text-[11px] text-slate-500">
                      {activeAuto ? 'Activo: responde knowledge comercial segura.' : 'Apagado: clasifica leads sin auto-responder.'}
                    </p>
                  </div>
                  <button
                    onClick={toggleGlobalAI}
                    disabled={saving || !tableAvailable}
                    className={`rounded-full px-4 py-2 text-xs font-extrabold text-white disabled:opacity-50 ${activeAuto ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                  >
                    {activeAuto ? 'AI ON' : 'AI OFF'}
                  </button>
                </div>
                {!tableAvailable && (
                  <p className="mt-2 rounded-lg bg-amber-100 px-3 py-2 text-[11px] font-semibold text-amber-800">
                    Falta aplicar la migracion de prospect leads para activar el agente.
                  </p>
                )}
              </div>

              <div className="mt-3 flex gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
                  <Search className="h-4 w-4 text-slate-400" />
                  <input
                    value={searchInput}
                    onChange={event => setSearchInput(event.target.value)}
                    placeholder="Buscar telefono, resumen, intent..."
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                  />
                </div>
                <button onClick={() => fetchLeadPage({ reset: true, offset: 0 })} className="rounded-xl bg-slate-100 px-3 py-2 text-slate-600 hover:bg-slate-200">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>

              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wide text-slate-400">
                  <Filter className="h-3.5 w-3.5" /> Estado
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {STATUS_FILTERS.map(([id, label]) => (
                    <button
                      key={id}
                      onClick={() => setStatus(id)}
                      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold ${status === id ? 'bg-[#B1336E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-wide text-slate-400">
                  <Clock className="h-3.5 w-3.5" /> Ventana WhatsApp
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {WINDOW_FILTERS.map(([id, label]) => (
                    <button
                      key={id}
                      onClick={() => setWindowBucket(id)}
                      className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold ${windowBucket === id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                  {error}
                </div>
              )}
            </div>

            <div onScroll={handleListScroll} className="min-h-0 flex-1 overflow-y-auto">
              {items.map(lead => (
                <LeadListItem
                  key={lead.id}
                  lead={lead}
                  selected={selectedLead?.id === lead.id}
                  onSelect={(nextLead) => {
                    setSelectedLead(nextLead);
                    setSelectedWorkspace(null);
                  }}
                />
              ))}

              {loading && (
                <div className="flex items-center justify-center gap-2 p-6 text-sm text-slate-500">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Cargando prospectos...
                </div>
              )}

              {!loading && !items.length && (
                <div className="flex flex-col items-center justify-center p-10 text-center text-slate-500">
                  <UserPlus className="mb-3 h-10 w-10 text-slate-300" />
                  <p className="text-sm font-semibold">Aun no hay prospectos en este filtro.</p>
                  <p className="mt-1 text-xs">Cuando entren WhatsApps desconocidos por ads apareceran aqui.</p>
                </div>
              )}

              {loadingMore && (
                <div className="flex items-center justify-center gap-2 p-4 text-xs font-semibold text-slate-500">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Cargando mas leads...
                </div>
              )}

              {!loading && !loadingMore && hasMore && (
                <button type="button" onClick={loadMore} className="w-full p-4 text-xs font-black text-[#B1336E] hover:bg-[#B1336E]/5">
                  Cargar mas prospectos
                </button>
              )}
            </div>
          </section>

          <main className="min-h-0 overflow-hidden">
            <ProspectLeadMiniChat
              lead={selectedLead}
              onLeadUpdated={handleLeadUpdated}
              onWorkspaceLoaded={(workspace) => {
                setSelectedWorkspace(workspace);
                if (workspace?.lead) handleLeadUpdated(workspace.lead);
              }}
            />
          </main>

          <aside className="hidden min-h-0 overflow-y-auto xl:block">
            <div className="space-y-3 pr-1">
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-blue-900">
                <div className="flex items-start gap-2">
                  <Info className="mt-0.5 h-4 w-4 shrink-0" />
                  <p className="text-xs font-semibold">
                    Libre hasta 24h desde el ultimo inbound WhatsApp. Despues solo plantillas Meta aprobadas para mantener cumplimiento.
                  </p>
                </div>
              </div>
              {selectedLead?.whatsapp_window?.templates_required && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p className="text-xs font-semibold">Ventana 24h cerrada: el composer libre queda bloqueado y el mini chat muestra plantillas.</p>
                  </div>
                </div>
              )}
              <div className="rounded-2xl border border-slate-200 bg-white p-3">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => toggleLeadAI(selectedLead)}
                    disabled={saving || !selectedLead}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-[11px] font-black text-slate-700 disabled:opacity-50"
                  >
                    <Bot className="h-3.5 w-3.5" /> {selectedLead?.ai_auto_enabled ? 'Lead AI ON' : 'Lead AI OFF'}
                  </button>
                  <button
                    type="button"
                    onClick={() => sendRegistration(selectedLead)}
                    disabled={saving || !selectedLead}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#B1336E]/10 px-3 py-1.5 text-[11px] font-black text-[#B1336E] disabled:opacity-50"
                  >
                    <Send className="h-3.5 w-3.5" /> Registro
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSegregation(selectedLead, { action: 'converted' })}
                    disabled={saving || !selectedLead}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-[11px] font-black text-emerald-700 disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Convertido
                  </button>
                </div>
              </div>
              <LeadDetailPanel lead={selectedLead} workspace={selectedWorkspace} onSegregate={handleSegregation} saving={saving} />
              <KnowledgePanel
                knowledge={knowledge}
                edits={knowledgeEdits}
                loading={knowledgeLoading}
                savingId={knowledgeSavingId}
                onEdit={(id, value) => setKnowledgeEdits(prev => ({ ...prev, [id]: value }))}
                onSave={saveKnowledge}
                onRefresh={loadKnowledge}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
